import type { Asset, AuthResponse, BootstrapData, CharacterProfile, Member, MemberRole, PromptTemplate } from './types';

const AUTH_TOKEN_KEY = 'hunktiger-auth-token';
const LAST_LOGIN_EMAIL_KEY = 'hunktiger-last-login-email';

export function getStoredLoginEmail() {
  return localStorage.getItem(LAST_LOGIN_EMAIL_KEY) ?? '';
}

export function storeLoginEmail(email: string) {
  localStorage.setItem(LAST_LOGIN_EMAIL_KEY, email.trim());
}

export function getStoredToken() {
  return localStorage.getItem(AUTH_TOKEN_KEY) ?? sessionStorage.getItem(AUTH_TOKEN_KEY) ?? '';
}

export function storeToken(token: string, remember: boolean) {
  localStorage.removeItem(AUTH_TOKEN_KEY);
  sessionStorage.removeItem(AUTH_TOKEN_KEY);
  (remember ? localStorage : sessionStorage).setItem(AUTH_TOKEN_KEY, token);
}

export function clearStoredToken() {
  localStorage.removeItem(AUTH_TOKEN_KEY);
  sessionStorage.removeItem(AUTH_TOKEN_KEY);
}

async function request<T>(path: string, options?: RequestInit): Promise<T> {
  const token = getStoredToken();
  const response = await fetch(path, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options?.headers,
    },
  });
  if (!response.ok) {
    const body = await response.json().catch(() => ({ message: '请求失败' }));
    throw new Error(body.message || '请求失败');
  }
  return response.json() as Promise<T>;
}

export const api = {
  login: (email: string, password: string, remember: boolean) => request<AuthResponse>('/api/login', { method: 'POST', body: JSON.stringify({ email, password, remember }) }),
  session: () => request<{ user: Member }>('/api/session'),
  logout: () => request<{ ok: boolean }>('/api/logout', { method: 'POST' }),
  bootstrap: () => request<BootstrapData>('/api/bootstrap'),
  saveDraft: (id: number, draft: string) => request(`/api/episodes/${id}/draft`, { method: 'PUT', body: JSON.stringify({ draft }) }),
  submitReview: (id: number) => request(`/api/episodes/${id}/submit`, { method: 'POST' }),
  publish: (id: number) => request(`/api/episodes/${id}/publish`, { method: 'POST' }),
  restore: (id: number) => request(`/api/episodes/${id}/restore`, { method: 'POST' }),
  createAsset: (payload: { title: string; category: string; description?: string; fileName?: string; imageData?: string }) => request<Asset>('/api/assets', { method: 'POST', body: JSON.stringify(payload) }),
  uploadAsset: (file: File, category: string, metadata?: { title?: string; description?: string }) => request<Asset>('/api/assets/upload', {
    method: 'POST',
    body: file,
    headers: {
      'Content-Type': file.type || 'application/octet-stream',
      'X-File-Name': encodeURIComponent(file.name),
      'X-Asset-Title': encodeURIComponent(metadata?.title?.trim() || file.name.replace(/\.[^.]+$/, '')),
      'X-Asset-Category': encodeURIComponent(category),
      'X-Asset-Description': encodeURIComponent(metadata?.description?.trim() || ''),
    },
  }),
  addAssetImage: (id: number, file: File) => request<Asset>(`/api/assets/${id}/images`, {
    method: 'POST',
    body: file,
    headers: {
      'Content-Type': file.type || 'application/octet-stream',
      'X-File-Name': encodeURIComponent(file.name),
    },
  }),
  updateAsset: (id: number, status: string) => request(`/api/assets/${id}`, { method: 'PATCH', body: JSON.stringify({ status }) }),
  updateAssetDetails: (id: number, payload: { title: string; category: string; status: string; description?: string }) => request(`/api/assets/${id}`, { method: 'PATCH', body: JSON.stringify(payload) }),
  updateCharacter: (id: number, profile: CharacterProfile) => request(`/api/assets/${id}`, { method: 'PATCH', body: JSON.stringify({ profile }) }),
  replaceAssetImage: (id: number, file: File) => request<Asset>(`/api/assets/${id}/image`, {
    method: 'PUT',
    body: file,
    headers: {
      'Content-Type': file.type || 'application/octet-stream',
      'X-File-Name': encodeURIComponent(file.name),
    },
  }),
  replaceAssetGalleryImage: (id: number, currentImage: string, file: File) => request<Asset>(`/api/assets/${id}/images`, {
    method: 'PUT',
    body: file,
    headers: {
      'Content-Type': file.type || 'application/octet-stream',
      'X-File-Name': encodeURIComponent(file.name),
      'X-Image-Path': encodeURIComponent(currentImage),
    },
  }),
  deleteAssetImage: (id: number, image: string) => request<Asset>(`/api/assets/${id}/images`, {
    method: 'DELETE',
    body: JSON.stringify({ image }),
  }),
  setAssetDisplayImage: (id: number, image: string) => request<Asset>(`/api/assets/${id}/display-image`, {
    method: 'PUT',
    body: JSON.stringify({ image }),
  }),
  deleteAsset: (id: number) => request(`/api/assets/${id}`, { method: 'DELETE' }),
  createPrompt: (payload: Omit<PromptTemplate, 'id' | 'uses' | 'updated'>) => request<PromptTemplate>('/api/prompts', { method: 'POST', body: JSON.stringify(payload) }),
  updatePrompt: (id: number, payload: Omit<PromptTemplate, 'id' | 'uses' | 'updated'>) => request<PromptTemplate>(`/api/prompts/${id}`, { method: 'PATCH', body: JSON.stringify(payload) }),
  recordPromptUse: (id: number) => request<PromptTemplate>(`/api/prompts/${id}/use`, { method: 'POST' }),
  inviteMember: (email: string, role: MemberRole) => request('/api/members', { method: 'POST', body: JSON.stringify({ email, role }) }),
  updateMember: (id: number, payload: { role?: MemberRole; status?: string }) => request(`/api/members/${id}`, { method: 'PATCH', body: JSON.stringify(payload) }),
};
